import React from 'react';
import { Box, Alert, AlertTitle, Button, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess, Refresh, Support } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Text from '../../../../../theme/text/Text';
import Shbutton from '../../../../../theme/buttons/Shbutton';
import { createErrorBoundaryHandler, getErrorDisplay, logError } from '../../utils/etailize/etailizeErrorHandling';

/**
 * EtailizeErrorBoundary Component
 * 
 * Error boundary specifically designed for Etailize authentication flow components.
 * Provides user-friendly error messages and recovery options.
 */
class EtailizeErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false,
            retryCount: 0
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error
        const { t } = this.props;
        const errorHandler = createErrorBoundaryHandler(
            (etailizeError) => {
                this.setState({ 
                    error: etailizeError,
                    errorInfo 
                });
                
                if (this.props.onError) {
                    this.props.onError(etailizeError);
                }
            },
            t || ((key) => key)
        );

        errorHandler(error, errorInfo);
    }

    handleRetry = () => {
        const newRetryCount = this.state.retryCount + 1;
        
        // Limit retry attempts
        if (newRetryCount > 3) {
            return;
        }

        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: newRetryCount
        });

        // Call parent retry handler if provided
        if (this.props.onRetry) {
            this.props.onRetry();
        }
    };

    handleContactSupport = () => {
        // Open support contact with error details
        const errorDetails = {
            error: this.state.error,
            url: window.location.href,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
        };

        // TODO: Integrate with actual support system
        console.log('Contact support with error details:', errorDetails);
        
        if (this.props.onContactSupport) {
            this.props.onContactSupport(errorDetails);
        }
    };

    toggleDetails = () => {
        this.setState({ showDetails: !this.state.showDetails });
    };

    render() {
        const { t = (key) => key, fallback, showRetry = true, showSupport = true } = this.props;

        if (this.state.hasError) {
            // Custom fallback component
            if (fallback) {
                return fallback(this.state.error, this.handleRetry);
            }

            const errorDisplay = this.state.error ? 
                getErrorDisplay(this.state.error, t) : 
                {
                    title: t('error_title_generic'),
                    message: t('component_error_generic'),
                    severity: 'HIGH',
                    recoveryOptions: []
                };

            return (
                <Box sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    border: '1px solid #f44336',
                    borderRadius: 2,
                    backgroundColor: '#ffebee'
                }}>
                    <Alert 
                        severity="error" 
                        sx={{ textAlign: 'left', mb: 2 }}
                    >
                        <AlertTitle>{errorDisplay.title}</AlertTitle>
                        {errorDisplay.message}
                    </Alert>

                    {/* Error Actions */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
                        {showRetry && this.state.retryCount < 3 && (
                            <Shbutton
                                variant="contained"
                                startIcon={<Refresh />}
                                onClick={this.handleRetry}
                            >
                                {t('retry')} ({3 - this.state.retryCount} {t('attempts_remaining')})
                            </Shbutton>
                        )}

                        {showSupport && (
                            <Shbutton
                                variant="outlined"
                                startIcon={<Support />}
                                onClick={this.handleContactSupport}
                            >
                                {t('contact_support')}
                            </Shbutton>
                        )}
                    </Box>

                    {/* Error Details Toggle */}
                    <Box>
                        <Button
                            variant="text"
                            size="small"
                            onClick={this.toggleDetails}
                            endIcon={this.state.showDetails ? <ExpandLess /> : <ExpandMore />}
                            sx={{ mb: 1 }}
                        >
                            {t('error_details')}
                        </Button>

                        <Collapse in={this.state.showDetails}>
                            <Box sx={{ 
                                textAlign: 'left', 
                                p: 2, 
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: '0.8em'
                            }}>
                                {this.state.error && (
                                    <>
                                        <Text variant="body2" fontWeight="bold">Error ID:</Text>
                                        <Text variant="body2" sx={{ mb: 1 }}>{this.state.error.id}</Text>
                                        
                                        <Text variant="body2" fontWeight="bold">Type:</Text>
                                        <Text variant="body2" sx={{ mb: 1 }}>{this.state.error.type}</Text>
                                        
                                        <Text variant="body2" fontWeight="bold">Timestamp:</Text>
                                        <Text variant="body2" sx={{ mb: 1 }}>{this.state.error.timestamp}</Text>

                                        {this.state.error.details && Object.keys(this.state.error.details).length > 0 && (
                                            <>
                                                <Text variant="body2" fontWeight="bold">Details:</Text>
                                                <Text variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                    {JSON.stringify(this.state.error.details, null, 2)}
                                                </Text>
                                            </>
                                        )}

                                        {this.state.errorInfo && (
                                            <>
                                                <Text variant="body2" fontWeight="bold">Component Stack:</Text>
                                                <Text variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {this.state.errorInfo.componentStack}
                                                </Text>
                                            </>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Etailize-specific guidance */}
                    <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        backgroundColor: '#e3f2fd',
                        borderRadius: 1,
                        border: '1px solid #2196f3'
                    }}>
                        <Text variant="body2" color="primary" fontWeight="medium" sx={{ mb: 1 }}>
                            🔧 {t('troubleshooting_tips')}
                        </Text>
                        <Text variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            • {t('troubleshooting_refresh_page')}
                        </Text>
                        <Text variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            • {t('troubleshooting_check_marketplace_status')}
                        </Text>
                        <Text variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            • {t('troubleshooting_verify_credentials')}
                        </Text>
                        <Text variant="body2" color="textSecondary">
                            • {t('troubleshooting_contact_support_persistent')}
                        </Text>
                    </Box>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default EtailizeErrorBoundary;